import { apiFetch, host } from "./fetch.api";
import { ThotType } from "../models/Thot.type";

import Store from "../redux/store";
import { saveChain } from "../redux/actions";
import { Platform } from "expo-modules-core";

const mediaMimeType = {
  mp4: "video/mp4",
  mov: "video/quicktime",
  qt: "video/quicktime",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webm: "video/webm",
  default: "application/octet-stream",
};

const imageMimeType = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  jpe: "image/jpeg",
  png: "image/png",
  default: "application/octet-stream",
};

function getFormData(which: "thot" | "thumbnail", uri: string, nameOverride?: string, typeOverride?: string): any {
  const uriParts = uri.split(".");
  const fileType = (uriParts[uriParts.length - 1] || "").toLowerCase();
  const name = nameOverride || (which + "." + fileType);

  const mimeType = which == "thot" ? mediaMimeType : imageMimeType;
  const type = typeOverride || (mimeType[fileType] ? mimeType[fileType] : mimeType.default);

  return {
    uri,
    type,
    name,
  };
}

export const apiCreateThot = ({
  uri,
  title,
  parent,
  thumbnail,
  mediaFile,
  imageFile,
  url,
  mediaKind,
  mediaName,
  mediaType,
  thumbnailRemote,
}: {
  uri?: string;
  title: string;
  parent?: string;
  thumbnail?: string;
  mediaFile?: File;
  imageFile?: File;
  url?: string;
  mediaKind?: "video" | "image" | "audio" | "url";
  mediaName?: string;
  mediaType?: string;
  thumbnailRemote?: string;
}): Promise<ThotType> => {
  const formData = new FormData();

  if (Platform.OS === "web") {
    if (mediaFile) formData.append("thot", mediaFile);
    if (imageFile) formData.append("thumbnail", imageFile);
  } else {
    if (uri) formData.append("thot", getFormData("thot", uri, mediaName, mediaType));
    if (thumbnail && !thumbnailRemote) formData.append("thumbnail", getFormData("thumbnail", thumbnail));
  }

  formData.append("title", title);
  if (parent) formData.append("parent", parent);
  if (url) formData.append("url", url);
  if (mediaKind) formData.append("media_kind", mediaKind);
  if (thumbnailRemote) formData.append("thumbnailRemote", thumbnailRemote);

  let options;
  if (Platform.OS == "web") {
    options = {
      method: "POST",
      setContentType: false,
      contentType: "multipart/form-data",
      data: formData,
    };
  } else {
    options = {
      method: "POST",
      contentType: "multipart/form-data",
      data: formData,
    };
  }
  return apiFetch<ThotType>("thots", options);
};

export const apiGetThot = (id: string): Promise<ThotType> => {
  return apiFetch("thots/" + id);
};

export const apiGetThotChildren = (id: string): Promise<ThotType[]> => {
  return apiFetch(`thots?parent=${id}&sort=-votes+-created_at`);
};

export const apiFindRootThotByUrl = (url: string): Promise<ThotType | null> => {
  return apiFetch<ThotType[]>(`thots`, {
    data: { url, sort: "-created_at", populate: "user" },
  }).then((items) => {
    const root = items.find((item) => !item.parent || item.root === item._id);
    return root || null;
  });
};

export const apiUpdateThot = (id: string, title: string): Promise<ThotType> => {
  return apiFetch("thots/" + id, {
    method: "PATCH",
    data: { title },
  });
};

export const apiDeleteThot = (id: string): Promise<ThotType> => {
  return apiFetch("thots/" + id, {
    method: "DELETE",
  });
};

export const apiLoadThotChain = (thot: ThotType): Promise<Boolean> => {
  return new Promise((resolve, reject) => {
    var id,
      root = null;

    if (thot.root) {
      if (typeof thot.root == "string") id = thot.root;
      else id = thot.root._id;
      root = thot.root;
    } else {
      id = thot._id;
      root = thot;
    }

    apiFetch(`thots?root=${id}&sort=-votes+-created_at&populate=user`)
      .then(async (response: ThotType[]) => {
        if (typeof root === "string") {
          root = await apiGetThot(thot.root + "");
        }

        Store.dispatch(saveChain(response, root, thot));
        resolve(true);
      })
      .catch(reject);
  });
};

export const apiGetThotUri = (id: string): string =>
  `${host}thots/${id}/stream`;
export const apiGetThotThumbnailUri = (id: string): string =>
  `${host}thots/${id}/thumbnail`;
